import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPhaseData } from '../../data/programData';
import { Play, Download, AlertTriangle, Info } from 'lucide-react';
import { Button } from '../../components/ui';

// Running program data - content matches PDF exactly
const PROGRAMS = {
  treadmill: {
    title: 'TREADMILL RUNNING PROGRAM',
    levels: [
      { level: 1, description: 'Alternate 0.1 mile walk / 0.1 mile jog - repeat for 2 mile total' },
      { level: 2, description: 'Alternate 0.1 mile walk / 0.2 mile jog - 2 mile total' },
      { level: 3, description: 'Alternate 0.1 mile walk / 0.3 mile jog - 2 mile total' },
      { level: 4, description: 'Alternate 0.1 mile walk / 0.4 mile jog - 2 mile total' },
      { level: 5, description: 'Jog full 2 miles' },
      { level: 6, description: 'Increase workout to 2 1/2 miles' },
      { level: 7, description: 'Increase workout to 3 miles' },
    ],
  },
  track: {
    title: 'TRACK RUNNING PROGRAM',
    levels: [
      { level: 1, description: 'Jog straights / Walk curves - 2 miles total' },
      { level: 2, description: 'Jog straights / Jog 1 curve every other lap' },
      { level: 3, description: 'Jog straights / Jog 1 curve every lap' },
      { level: 4, description: 'Jog 1 3/4 lap / Walk curve' },
      { level: 5, description: 'Jog all laps' },
      { level: 6, description: 'Increase workout to 2 1/2 miles' },
      { level: 7, description: 'Increase workout to 3 miles' },
    ],
  },
  minutes: {
    title: 'MINUTES RUNNING PROGRAM',
    levels: [
      { level: 1, description: 'Walk 2min / Jog 1min – 20 min total' },
      { level: 2, description: 'Walk 2min / Jog 2min – 16 min total' },
      { level: 3, description: 'Walk 2min / Jog 3min – 15 min total' },
      { level: 4, description: 'Walk 1min / Jog 4min – 15 min total' },
      { level: 5, description: 'Jog 15min' },
      { level: 6, description: 'Jog 20min' },
      { level: 7, description: 'Jog 25min' },
    ],
  },
};

const INSTRUCTIONS = [
  'Mandatory 2-day rest between workouts for first two weeks.',
  'Do not advance more than 2 levels per week.',
  'Two days rest mandatory between levels 1, 2, and 3 workouts.',
  'One day rest mandatory between levels 4-7 workouts.',
];

const SORENESS_RULES = [
  { condition: 'If sore during warm-up', action: 'take 2 days off and drop down 1 level.' },
  { condition: 'If sore during workout', action: 'take 1 day off and drop down 1 level.' },
  { condition: 'If sore after workout', action: 'stay at same level.' },
];

export function ReturnToRunPage() {
  const { phaseId } = useParams();
  const phaseData = getPhaseData(parseInt(phaseId));
  const [activeTab, setActiveTab] = useState('treadmill');

  const pdfUrl = '/documents/running-program.pdf';

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'ACL-Care-Running-Program.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const activeProgram = PROGRAMS[activeTab];

  return (
    <div className="max-w-2xl mx-auto p-4 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Play className="h-6 w-6 text-primary mr-2" />
          <h1 className="text-2xl font-bold text-gray-900">Return to Run Program</h1>
        </div>
      </div>

      {/* Program Type Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-4">
        <div className="flex border-b border-gray-200">
          {Object.entries(PROGRAMS).map(([key, program]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`
                flex-1 px-4 py-3 text-sm font-medium transition-colors
                ${activeTab === key
                  ? 'bg-primary/5 text-primary border-b-2 border-primary -mb-px'
                  : 'text-gray-600 hover:bg-gray-50'
                }
              `}
            >
              {key.charAt(0).toUpperCase() + key.slice(1)}
            </button>
          ))}
        </div>

        {/* Program Content */}
        <div className="p-4">
          <h2 className="font-semibold text-gray-900 mb-4 text-center">
            {activeProgram.title}
          </h2>

          {/* Levels */}
          <div className="space-y-2">
            {activeProgram.levels.map((item) => (
              <div
                key={item.level}
                className="flex items-start p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                  <span className="text-sm font-bold text-primary">{item.level}</span>
                </div>
                <div className="flex-1 pt-1">
                  <span className="text-sm font-medium text-gray-500 mr-2">LEVEL {item.level}</span>
                  <p className="text-sm text-gray-900 mt-0.5">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex items-center mb-3">
          <Info className="h-5 w-5 text-blue-600 mr-2" />
          <h3 className="font-semibold text-gray-900">Instructions</h3>
        </div>
        <ul className="space-y-2">
          {INSTRUCTIONS.map((instruction, idx) => (
            <li key={idx} className="flex items-start text-sm text-gray-700">
              <span className="h-1.5 w-1.5 rounded-full bg-gray-400 mt-2 mr-3 flex-shrink-0" />
              {instruction}
            </li>
          ))}
        </ul>
      </div>

      {/* Soreness Rules */}
      <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
        <div className="flex items-center mb-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mr-2" />
          <h3 className="font-semibold text-amber-900">Soreness Rules</h3>
        </div>
        <p className="text-xs text-amber-700 mb-3 italic">
          Soreness refers to knee joint soreness, not muscle soreness
        </p>
        <ul className="space-y-2">
          {SORENESS_RULES.map((rule, idx) => (
            <li key={idx} className="flex items-start text-sm text-amber-800">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 mt-2 mr-3 flex-shrink-0" />
              <span>
                <strong>{rule.condition}</strong>, {rule.action}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}